import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  OnInit,
  Output,
  inject,
  signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from './../../../../environments/environment';
import { LoggerService } from './../../../services/logger.service';

/**
 * Jira Options
 * ------------
 * Sub-formulario montado dinámicamente por el ExportPanel cuando el usuario
 * elige Jira como destino. Recoge:
 *   - Project destino (select cargado de /api/integrations/jira/projects).
 *   - Issue type (select dependiente del project elegido).
 *   - Prefix opcional para el summary del issue.
 *
 * Emite `optionsChanged` cada vez que el usuario modifica algo; el panel
 * acumula y lo manda al backend al pulsar EXPORTAR.
 *
 * `reload()` es público — el panel lo invoca tras conectar Jira si la lista
 * estaba vacía por falta de conexión previa.
 *
 * @author Marcos Cordo
 * @version 07/06/2026_1
 */

export interface JiraIssueTypeDto {
  id: string;
  name: string;
  iconUrl?: string;
  subtask?: boolean;
}

export interface JiraProjectDto {
  id: string;
  key: string;
  name: string;
  avatarUrl?: string;
  issueTypes: JiraIssueTypeDto[];
}

export interface JiraExporterOptions {
  projectKey: string;
  issueTypeName: string;
  summaryPrefix?: string;
}

@Component({
  selector: 'app-jira-options',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './jira-options.component.html',
  styleUrls: ['./jira-options.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class JiraOptionsComponent implements OnInit {
  /** Emite las opciones actuales cada vez que el usuario las modifica. */
  @Output() optionsChanged = new EventEmitter<JiraExporterOptions>();
  /** Emite true cuando las opciones son válidas para enviar el job. */
  @Output() validityChanged = new EventEmitter<boolean>();

  loading = signal(true);
  errorMessage = signal<string | null>(null);
  projects = signal<JiraProjectDto[]>([]);

  selectedProjectKey: string | null = null;
  selectedIssueTypeName: string | null = null;
  summaryPrefix = '';

  private http = inject(HttpClient);
  private logger = inject(LoggerService);

  async ngOnInit(): Promise<void> {
    await this.reload();
  }

  /**
   * Carga proyectos del backend. Idempotente — re-invocable cuando el chip
   * de conexión emite (connectionChanged).
   */
  async reload(): Promise<void> {
    this.loading.set(true);
    this.errorMessage.set(null);
    try {
      const resp = await firstValueFrom(
        this.http.get<JiraProjectDto[]>(`${environment.api_service_integrations}/jira/projects`)
      );
      const list = Array.isArray(resp) ? resp : [];
      this.projects.set(list);

      // Auto-selección razonable: primer proyecto + primer issue type no-subtask.
      if (list.length > 0) {
        this.selectedProjectKey = list[0].key;
        const firstUsableType = list[0].issueTypes.find(t => !t.subtask) || list[0].issueTypes[0];
        this.selectedIssueTypeName = firstUsableType ? firstUsableType.name : null;
      } else {
        this.selectedProjectKey = null;
        this.selectedIssueTypeName = null;
      }
      this.emit();
    } catch (err: any) {
      const status = err?.status;
      // 412 = backend dice "sin tokens GitLab/Jira" (precondición no cumplida).
      // 401 se mantiene por compat defensiva (cualquier cliente antiguo o caso
      // legacy donde el backend devuelva 401 por sesión expirada de verdad
      // ya habrá sido capturado por el interceptor global antes de llegar aquí).
      if (status === 412 || status === 401) {
        this.errorMessage.set('Sin conexión activa con Jira. Usa el botón "CONECTAR JIRA".');
      } else if (status === 403) {
        this.errorMessage.set('Sin permisos para acceder a Jira.');
      } else {
        this.logger.error('[JiraOptions] No se pudieron cargar proyectos:', err);
        this.errorMessage.set('No se pudieron cargar los proyectos.');
      }
      this.projects.set([]);
      this.emit();
    } finally {
      this.loading.set(false);
    }
  }

  /** Issue types disponibles para el proyecto seleccionado. */
  get availableIssueTypes(): JiraIssueTypeDto[] {
    const p = this.projects().find(x => x.key === this.selectedProjectKey);
    return p ? p.issueTypes : [];
  }

  onProjectChange(): void {
    // Al cambiar de proyecto, re-elegir el primer tipo no-subtask.
    const types = this.availableIssueTypes;
    if (types.length > 0) {
      const firstUsable = types.find(t => !t.subtask) || types[0];
      this.selectedIssueTypeName = firstUsable.name;
    } else {
      this.selectedIssueTypeName = null;
    }
    this.emit();
  }

  onIssueTypeChange(): void { this.emit(); }
  onPrefixChange(): void    { this.emit(); }

  private emit(): void {
    const valid = !!(this.selectedProjectKey && this.selectedIssueTypeName);
    if (valid) {
      this.optionsChanged.emit({
        projectKey: this.selectedProjectKey!,
        issueTypeName: this.selectedIssueTypeName!,
        summaryPrefix: this.summaryPrefix?.trim() || undefined
      });
    }
    this.validityChanged.emit(valid);
  }
}